import { ChangeEvent, FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { Button, Col, ConfigProvider, Divider, Input, Row } from 'antd';
import { AxiosResponse, isAxiosError } from 'axios';
import {
    TbArrowRight,
    TbChevronLeft,
    TbCircleXFilled,
    TbClockCheck,
    TbCloudLock,
    TbFingerprintScan,
    TbMail,
    TbMailSpark,
    TbMailX,
    TbUser,
    TbX,
} from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { unauthorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformAlert } from '@/shared/components/alerts';
import PlatformActionCard from '@/shared/components/cards/PlatformActionCard';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useAuthStore } from '@/stores/auth';
import { appendRedirectToPath, appendSearchParamsToPath, getRedirectFromSearch, sanitizePlanKey } from '@/utilities';

import { GoogleIcon, MicrosoftIcon } from './components/ProviderIcons';
import { AuthMethod, EmailAuthMode, getLastAuthMethod, isValidEmail, lastAuthMethodStorageKey } from './configuration';

interface AuthButtonGroupProps {
    route: string;
}

interface PasskeyAuthenticationResponse {
    redirectURL?: string;
    verified: boolean;
}

interface AuthFormValues {
    emailAddress?: string;
    firstName?: string;
    lastName?: string;
}

const getQueryParam = (param: string) => {
    const searchParams = new URLSearchParams(window.location.search);

    return searchParams.get(param);
};

const AuthButtonGroup: FC<AuthButtonGroupProps> = ({ route }): JSX.Element => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = getRedirectFromSearch(location.search);
    const selectedPlan = sanitizePlanKey(searchParams.get('plan'));
    const { referralContext } = useAuthStore();
    const referralCode =
        referralContext.referralCode ||
        searchParams.get('r') ||
        searchParams.get('referral') ||
        searchParams.get('referralId') ||
        searchParams.get('referralLinkId') ||
        searchParams.get('ref') ||
        '';
    const dubClickId = referralContext.dubClickId || searchParams.get('dub_id') || '';
    const apiBase = import.meta.env.PROD ? 'https://api.countercyclical.io' : 'http://localhost:8000';

    const getProviderURL = (provider: 'google' | 'microsoft') => {
        const url = new URL(`/auth/${provider}`, apiBase);

        if (redirectPath) {
            url.searchParams.set('redirect', redirectPath);
        }

        if (selectedPlan) {
            url.searchParams.set('plan', selectedPlan);
        }

        if (referralCode) {
            url.searchParams.set('r', referralCode);
        }

        if (dubClickId) {
            url.searchParams.set('dub_id', dubClickId);
        }

        return url.toString();
    };

    const [error, setError] = useState('');

    useEffect(() => {
        const failureRedirect = getQueryParam('failureRedirect');
        const authenticationMethodDenied = getQueryParam('authenticationMethodDenied');

        if (authenticationMethodDenied) {
            setError(
                `${authenticationMethodDenied} authentication is not allowed for that workspace. Use an authorized method to continue.`,
            );

            return;
        }

        if (failureRedirect) {
            setError(failureRedirect);
        }
    }, []);

    const { token } = useAntDesignTheme();

    const getErrorDescription = () => {
        if (error === 'true') {
            return 'Generic email addresses are not allowed on Countercyclical. Please log in with a work or school email instead.';
        }

        return error;
    };

    const getErrorTitle = () => {
        if (error === 'true') {
            return 'Email not allowed';
        }

        return 'Authentication failed';
    };

    const getPasskeyErrorMessage = (incomingError: unknown) => {
        if (incomingError instanceof Error && incomingError.name === 'NotAllowedError') {
            return 'Passkey sign-in was canceled or timed out.';
        }

        if (isAxiosError(incomingError) && typeof incomingError.response?.data?.message === 'string') {
            return incomingError.response.data.message;
        }

        return 'We could not verify that passkey. Try again or use a magic link.';
    };

    const [loading, setLoading] = useState<boolean>(true);
    const [passkeysSupported, setPasskeysSupported] = useState<boolean>(false);
    const [lastAuthMethod, setLastAuthMethod] = useState<AuthMethod | null>(getLastAuthMethod);
    const [emailAuthMode, setEmailAuthMode] = useState<EmailAuthMode | null>(null);
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');

    const validSubmission =
        emailAuthMode !== null &&
        isValidEmail(emailAddress) &&
        emailAddress !== '' &&
        (route !== '/register' || emailAuthMode !== 'magic' || (firstName.trim() !== '' && lastName.trim() !== ''));

    const navigate = useNavigate();

    useEffect(() => {
        setPasskeysSupported(browserSupportsWebAuthn());
        setLoading(false);
    }, []);

    const rememberLastAuthMethod = (authMethod: AuthMethod) => {
        localStorage.setItem(lastAuthMethodStorageKey, authMethod);
        setLastAuthMethod(authMethod);
    };

    const loginWithPasskey = async (incomingEmailAddress = emailAddress) => {
        const normalizedEmailAddress = incomingEmailAddress.trim().toLowerCase();

        if (!isValidEmail(normalizedEmailAddress)) {
            setError('Enter your email address before using a passkey.');

            return;
        }

        if (!passkeysSupported) {
            setError('Passkeys are not available in this browser.');

            return;
        }

        try {
            setError('');
            setLoading(true);

            const optionsResponse = await unauthorizedAxiosInstance.post(
                '/auth/passkey/generate-authentication-options',
                {
                    emailAddress: normalizedEmailAddress,
                    redirect: redirectPath,
                    plan: selectedPlan,
                },
                { withCredentials: true },
            );

            const authenticationResponse = await startAuthentication({ optionsJSON: optionsResponse.data });
            const verificationResponse = await unauthorizedAxiosInstance.post<PasskeyAuthenticationResponse>(
                '/auth/passkey/verify-authentication',
                authenticationResponse,
                { withCredentials: true },
            );

            if (verificationResponse.data.verified && verificationResponse.data.redirectURL) {
                rememberLastAuthMethod('passkey');
                window.location.assign(verificationResponse.data.redirectURL);

                return;
            }

            setError('We could not verify that passkey. Try again or use a magic link.');
        } catch (incomingError) {
            captureException(incomingError);
            setError(getPasskeyErrorMessage(incomingError));
        } finally {
            setLoading(false);
        }
    };

    const getSamlErrorMessage = (incomingError: unknown) => {
        if (isAxiosError(incomingError) && typeof incomingError.response?.data?.message === 'string') {
            return incomingError.response.data.message;
        }

        if (incomingError instanceof Error) {
            return incomingError.message;
        }

        return 'SAML SSO is not available for this email domain.';
    };

    const loginWithSamlSso = async (incomingEmailAddress = emailAddress) => {
        const normalizedEmailAddress = incomingEmailAddress.trim().toLowerCase();

        if (!isValidEmail(normalizedEmailAddress)) {
            setError('Enter your work email address before using SAML SSO.');

            return;
        }

        try {
            setError('');
            setLoading(true);

            const response = await unauthorizedAxiosInstance.post<{ authorizationUrl: string }>('/auth/sso', {
                email: normalizedEmailAddress,
                redirect: redirectPath,
                plan: selectedPlan,
            });

            if (response.data.authorizationUrl) {
                rememberLastAuthMethod('saml');
                window.location.assign(response.data.authorizationUrl);

                return;
            }

            setError('SAML SSO is not available for this email domain.');
            setLoading(false);
        } catch (incomingError) {
            captureException(incomingError);
            setError(getSamlErrorMessage(incomingError));
            setLoading(false);
        }
    };

    const sendMagicLink = async (values: AuthFormValues) => {
        const normalizedEmailAddress = String(values.emailAddress ?? emailAddress)
            .trim()
            .toLowerCase();
        const valid = isValidEmail(normalizedEmailAddress);
        const hasRequiredRegistrationFields =
            route !== '/register' || (firstName.trim() !== '' && lastName.trim() !== '');

        if (!valid || !hasRequiredRegistrationFields) {
            return;
        }

        setLoading(true);

        const payload =
            route === '/register'
                ? {
                      destination: normalizedEmailAddress,
                      firstName,
                      lastName,
                      redirect: redirectPath,
                      plan: selectedPlan,
                      referralCode,
                      dubClickId,
                  }
                : { destination: normalizedEmailAddress, redirect: redirectPath, plan: selectedPlan };

        try {
            const response: AxiosResponse = await unauthorizedAxiosInstance.post('/auth/magic', payload);

            if (response?.status === 200 && response?.data?.success) {
                rememberLastAuthMethod('magic');
                navigate(
                    appendSearchParamsToPath(
                        appendRedirectToPath(`/magiclink/success?emailAddress=${normalizedEmailAddress}`, redirectPath),
                        { plan: selectedPlan, r: referralCode, dub_id: dubClickId },
                    ),
                );

                return;
            }
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const emailAuthOptions: {
        description: string;
        disabled?: boolean;
        icon: JSX.Element;
        key: EmailAuthMode;
        label: string;
    }[] = [
        {
            key: 'magic',
            icon: <TbMailSpark size='1.125rem' />,
            label: 'Continue with Magic Link',
            description: 'Receive a secure sign-in link in your email inbox.',
        },
        ...(route !== '/register'
            ? [
                  {
                      key: 'passkey' as EmailAuthMode,
                      icon: <TbFingerprintScan size='1.125rem' />,
                      label: 'Continue with Passkey',
                      description: passkeysSupported
                          ? 'Use a registered passkey associated with your email address.'
                          : 'Passkeys are not available in this browser.',
                      disabled: !passkeysSupported,
                  },
              ]
            : []),
        {
            key: 'saml',
            icon: <TbCloudLock size='1.125rem' />,
            label: 'Continue with SAML SSO',
            description: 'Use your organization SAML connection for single sign-on.',
        },
    ];

    const selectedEmailAuthOption = emailAuthOptions.find((option) => option.key === emailAuthMode);

    const renderLastAuthMethodBadge = (authMethod: AuthMethod, children: JSX.Element) => {
        if (lastAuthMethod !== authMethod) {
            return children;
        }

        return (
            <div style={{ position: 'relative', width: '100%' }}>
                {children}
                <span
                    style={{
                        alignItems: 'center',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadiusSM,
                        color: token.colorTextSecondary,
                        display: 'inline-flex',
                        fontSize: token.fontSizeSM,
                        fontWeight: 400,
                        gap: '0.25rem',
                        height: '1.25rem',
                        insetInlineEnd: '0.75rem',
                        lineHeight: 1,
                        padding: '0 0.25rem',
                        pointerEvents: 'none',
                        position: 'absolute',
                        top: '-0.625rem',
                        zIndex: 1,
                    }}
                >
                    <TbClockCheck size='0.75rem' style={{ color: token.colorTextTertiary }} />
                    Last used
                </span>
            </div>
        );
    };

    return (
        <>
            {error !== '' && (
                <PlatformAlert
                    closable
                    type='error'
                    closeIcon={<TbX size='1rem' />}
                    showIcon
                    icon={error === 'true' ? <TbMailX /> : <TbFingerprintScan />}
                    message={getErrorTitle()}
                    description={getErrorDescription()}
                    style={{ marginBottom: '1.5rem' }}
                />
            )}
            <ConfigProvider
                theme={{
                    components: {
                        Button: {
                            defaultActiveBg: token.colorBgBase,
                            defaultActiveBorderColor: token.colorBorder,
                            defaultActiveColor: token.colorText,
                            defaultHoverBg: token.colorBgBase,
                            defaultHoverBorderColor: token.colorBorder,
                            defaultHoverColor: token.colorText,
                        },
                    },
                }}
            >
                {renderLastAuthMethodBadge(
                    'google',
                    <Button
                        disabled={loading}
                        size='large'
                        type='default'
                        block
                        icon={
                            <GoogleIcon
                                style={{
                                    marginRight: '0.125rem',
                                }}
                            />
                        }
                        onClick={() => {
                            rememberLastAuthMethod('google');
                            window.location.href = getProviderURL('google');
                        }}
                        style={{ borderColor: token.colorBorder }}
                    >
                        Continue with Google
                    </Button>,
                )}
                <div style={{ marginTop: '0.75rem' }}>
                    {renderLastAuthMethodBadge(
                        'microsoft',
                        <Button
                            disabled={loading}
                            type='default'
                            size='large'
                            block
                            style={{ borderColor: token.colorBorder }}
                            icon={
                                <MicrosoftIcon
                                    style={{
                                        verticalAlign: '-0.2rem',
                                        marginRight: '0.125rem',
                                    }}
                                />
                            }
                            onClick={() => {
                                rememberLastAuthMethod('microsoft');
                                window.location.href = getProviderURL('microsoft');
                            }}
                        >
                            Continue with Microsoft
                        </Button>,
                    )}
                </div>
            </ConfigProvider>
            <Divider style={{ margin: '0.75rem 0' }}>Or</Divider>
            {emailAuthMode === null ? (
                <Row gutter={[0, 8]}>
                    {emailAuthOptions.map((option) => (
                        <Col span={24} key={option.key}>
                            {renderLastAuthMethodBadge(
                                option.key,
                                <PlatformActionCard
                                    description={option.description}
                                    disabled={loading || option.disabled}
                                    icon={option.icon}
                                    label={option.label}
                                    trailing={
                                        <TbArrowRight
                                            size='1.125rem'
                                            style={{ color: token.colorTextTertiary, verticalAlign: '-0.25rem' }}
                                        />
                                    }
                                    onPress={() => {
                                        setError('');
                                        setEmailAuthMode(option.key);
                                    }}
                                />,
                            )}
                        </Col>
                    ))}
                </Row>
            ) : (
                <Form
                    requiredMark={false}
                    layout='vertical'
                    onFinish={(values: AuthFormValues) => {
                        const normalizedEmailAddress = String(values.emailAddress ?? emailAddress)
                            .trim()
                            .toLowerCase();

                        if (emailAuthMode === 'passkey') {
                            void loginWithPasskey(normalizedEmailAddress);

                            return;
                        }

                        if (emailAuthMode === 'saml') {
                            void loginWithSamlSso(normalizedEmailAddress);

                            return;
                        }

                        void sendMagicLink(values);
                    }}
                >
                    <Button
                        type='text'
                        size='small'
                        icon={<TbChevronLeft size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                        style={{
                            color: token.colorTextTertiary,
                            marginBottom: '0.5rem',
                        }}
                        onClick={() => {
                            setError('');
                            setEmailAuthMode(null);
                        }}
                    >
                        Use another method
                    </Button>
                    <div style={{ marginBottom: '0.75rem' }}>
                        <Row align='middle' gutter={[8, 0]} wrap={false}>
                            <Col flex='none'>
                                <span style={{ color: token.colorTextTertiary, display: 'flex' }}>
                                    {selectedEmailAuthOption?.icon}
                                </span>
                            </Col>
                            <Col flex='auto' style={{ minWidth: 0 }}>
                                <strong>{selectedEmailAuthOption?.label}</strong>
                                <div>
                                    <small style={{ color: token.colorTextTertiary }}>
                                        {selectedEmailAuthOption?.description}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    </div>
                    {route === '/register' && emailAuthMode === 'magic' && (
                        <Row gutter={[16, 0]}>
                            <Col span={12}>
                                <Form.Item
                                    name='firstName'
                                    style={{ paddingTop: 0, marginBottom: '0.5rem' }}
                                    required
                                    label='First Name'
                                >
                                    <Input
                                        disabled={loading}
                                        prefix={
                                            <TbUser
                                                size='1.125rem'
                                                style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }}
                                            />
                                        }
                                        placeholder='Name...'
                                        size='large'
                                        allowClear={{
                                            clearIcon: (
                                                <TbCircleXFilled
                                                    size='1.125rem'
                                                    style={{ verticalAlign: '-0.25rem' }}
                                                />
                                            ),
                                        }}
                                        value={firstName}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setFirstName(e.target.value);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name='lastName'
                                    style={{ paddingTop: 0, marginBottom: '0.5rem' }}
                                    required
                                    label='Last Name'
                                >
                                    <Input
                                        disabled={loading}
                                        prefix={
                                            <TbUser
                                                size='1.125rem'
                                                style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }}
                                            />
                                        }
                                        placeholder='Name...'
                                        size='large'
                                        allowClear={{
                                            clearIcon: (
                                                <TbCircleXFilled
                                                    size='1.125rem'
                                                    style={{ verticalAlign: '-0.25rem' }}
                                                />
                                            ),
                                        }}
                                        value={lastName}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setLastName(e.target.value);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}
                    <Form.Item name='emailAddress' style={{ marginBottom: 0 }} required label='Email Address'>
                        <Input
                            disabled={loading}
                            prefix={
                                <TbMail
                                    size='1.125rem'
                                    style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }}
                                />
                            }
                            placeholder='Email...'
                            size='large'
                            allowClear={{
                                clearIcon: <TbCircleXFilled size='1.125rem' style={{ verticalAlign: '-0.25rem' }} />,
                            }}
                            value={emailAddress}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setEmailAddress(e.target.value);
                            }}
                        />
                    </Form.Item>
                    <Button
                        icon={selectedEmailAuthOption?.icon}
                        htmlType='submit'
                        block
                        size='large'
                        type='primary'
                        style={{ marginTop: '0.75rem' }}
                        disabled={loading || !validSubmission}
                        loading={loading}
                    >
                        {!loading ? (
                            <>
                                {selectedEmailAuthOption?.label}
                                <TbArrowRight
                                    size='1.125rem'
                                    style={{ verticalAlign: '-0.25rem', marginLeft: '0.25rem' }}
                                />
                            </>
                        ) : (
                            <></>
                        )}
                    </Button>
                </Form>
            )}
        </>
    );
};

export default AuthButtonGroup;
